import React from 'react';
import { Info, TrendingUp, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PRICING_GUIDE, getCategoryLabel, getLessonTypeLabel } from '@/config/pricingGuide';

export const PricingGuideTooltip = ({ category, lessonType }) => {
  const guide = PRICING_GUIDE[lessonType]?.[category];
  
  if (!guide) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="ml-2 text-purple-600 hover:text-purple-800">
            <Info className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs p-4 bg-white border shadow-lg">
          <div className="space-y-2">
            <h4 className="font-semibold text-purple-800 flex items-center">
              <TrendingUp className="h-4 w-4 mr-1" />
              Fiyatlandırma Rehberi
            </h4>
            <p className="text-sm text-gray-600">
              {getCategoryLabel(category)} - {getLessonTypeLabel(lessonType)}
            </p>
            <div className="bg-purple-50 rounded p-2 text-sm">
              <div className="flex justify-between">
                <span>Minimum:</span>
                <span className="font-medium">{guide.min}₺</span>
              </div>
              <div className="flex justify-between text-purple-700 font-medium">
                <span>Önerilen:</span>
                <span>{guide.recommended}₺</span>
              </div>
              <div className="flex justify-between">
                <span>Maksimum:</span>
                <span className="font-medium">{guide.max}₺</span>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Bu aralık, benzer derslerin piyasa fiyatlarına göre belirlenmiştir.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const PriceWarning = ({ price, category, lessonType }) => {
  const guide = PRICING_GUIDE[lessonType]?.[category];
  
  if (!guide || !price) return null;
  
  const priceNum = parseFloat(price);
  
  if (priceNum < guide.min) {
    return (
      <div className="flex items-start gap-2 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
        <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-yellow-800">
          Bu fiyat önerilen aralığın altında ({guide.min}₺ - {guide.max}₺). 
          Düşük fiyat, kalite algısını olumsuz etkileyebilir.
        </p>
      </div>
    );
  }
  
  if (priceNum > guide.max) {
    return (
      <div className="flex items-start gap-2 mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
        <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-orange-800">
          Bu fiyat önerilen aralığın üstünde ({guide.min}₺ - {guide.max}₺). 
          Yüksek fiyat, satış oranını düşürebilir.
        </p>
      </div>
    );
  }
  
  if (priceNum >= guide.min && priceNum <= guide.max) {
    return (
      <div className="flex items-center gap-2 mt-2 text-sm text-green-700">
        <CheckCircle2 className="h-4 w-4" />
        <span>Fiyat önerilen aralıkta ✓</span>
      </div>
    );
  }
  
  return null;
};

export default PricingGuideTooltip;
